/* eslint-disable no-extend-native */
import moment from 'moment-business-days';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components/macro';
import { WxDialog, WxLabel, WxText } from '../components';
import { CartItem } from '../components/pages/cart/CartItem';
import { AddressDisplay } from '../components/ui/address/AddressDisplay';
import { CustomNav } from '../components/ui/customNav/CustomNav';
import { OrderBar } from '../components/ui/orderBar/OrderBar';
import useCreateOrder, {
  CreateOrderInput,
} from '../hooks/api/mutations/useCreateOrder';
import useDeliveryPromess from '../hooks/api/queries/useDeliveryPromesses';
import { debitMilesCompleteErrors, debitMilesErrors } from '../hooks/api/queries/useOrder';
import { useAddressContext } from '../hooks/context/useAddressContext';
import { useCartContext } from '../hooks/context/useCartContext';
import { __ } from '../i18n/l10n';
import { breakpoint } from '../styles/constants';
import { SLUG_CONFIRMATION } from '../styles/slugs';
import { appendCommitHash } from '../utils/appendHash';

export const Recap = () => {
  const navigate = useNavigate();
  const { cart, serverCartItems, verifyCart } = useCartContext();
  const { accountAddress, address, addressSelected } = useAddressContext();
  const [recapCheck, setRecapCheck] = useState<boolean>(false);
  const [orderFailed, setOrderFailed] = useState<boolean>(false);
  const [orderFailedMessage, setOrderFailedMessage] = useState<string>('');
  const { data } = useDeliveryPromess({
    variants: cart?.items.map((item) => ({
      variantId: item.variantId,
      quantity: item.quantity,
    })),
  });

  const { mutate, isLoading } = useCreateOrder();

  useEffect(() => {
    verifyCart();
  }, [verifyCart]);

  if (
    !accountAddress ||
    !address ||
    !serverCartItems ||
    cart.totalArticles === 0 ||
    cart.items.length === 0
  ) {
    navigate(appendCommitHash('/'));
    return null;
  }

  const orderAddress =
    addressSelected === 'WINA'
      ? accountAddress
      : {
          ...address,
          firstname: accountAddress.firstname,
          lastname: accountAddress.lastname,
          email: accountAddress.email,
          mobilePhone: accountAddress.mobilePhone,
        };

  const createOrderInput: CreateOrderInput = {
    totalMilesAmount: cart.totalMilesAmount,
    address: orderAddress,
    items: cart.items.map((item) => ({
      variantId: item.variantId,
      quantity: item.quantity,
    })),
  };

  const isNotDemat =
    cart.items.filter((i) => i.dematerialized === 0).length > 0;

  return (
    <Wrapper>
      {orderFailed && (
        <WxDialog
          title={__('Unable to order')}
          content={debitMilesErrors[orderFailedMessage]
            ? debitMilesCompleteErrors[orderFailedMessage] ? debitMilesCompleteErrors[orderFailedMessage] : __('Your order could not be completed because of %1$s', [
                debitMilesErrors[orderFailedMessage],
              ])
            : __('Your order could not be completed')}
          open={orderFailed}
          setOpen={setOrderFailed}
          onClose={() => navigate(appendCommitHash(`/`))}
          noCancel
        />
      )}
      <CustomNav
        label={__('Summary')}
        navStyle="chevron"
        forceDestination={!isNotDemat ? appendCommitHash('/cart') : undefined}
        forceDesktopLabelCenter
      />
      <DivContent>
        <DivItems>
          <WxLabel label={__('Items')} active />
          <Ul>
            {serverCartItems.map((item, index) => {
              return (
                <li key={item.variantId}>
                  {data?.[item.variantId].deliveryPromess !== undefined &&
                    data?.[item.variantId].deliveryPromess !== 0 &&
                    typeof data?.[item.variantId].deliveryPromess ===
                      'number' &&
                    isFinite(data?.[item.variantId].deliveryPromess) && (
                      <MarginBottom>
                        <WxText
                          text={`${__('Estimated delivery date')} : ${moment(
                            moment(new Date()).format('DD/MM/YYYY'),
                            'DD-MM-YYYY',
                          )
                            .businessAdd(data?.[item.variantId].deliveryPromess)
                            .format('DD MMMM YYYY')}`}
                          bold
                          darkMode
                        />
                      </MarginBottom>
                    )}
                  <CartItem
                    item={item}
                    withActions={false}
                    quantity={cart.items[index].quantity}
                  />
                </li>
              );
            })}
          </Ul>
        </DivItems>
        {isNotDemat && (
          <DivAddress>
            <div>
              <WxLabel label={__('Address')} active />
              <AddressDisplay address={orderAddress} />
            </div>
          </DivAddress>
        )}
      </DivContent>
      <OrderBar
        label={__('Pay')}
        active={!isLoading}
        recapCheck={recapCheck}
        setRecapCheck={setRecapCheck}
        onClick={() => {
          mutate(createOrderInput, {
            onSuccess: ({ orderId }) => {
              navigate(appendCommitHash(`/${SLUG_CONFIRMATION}`), {
                state: { orderId },
              });
            },
            onError: (params: any) => {
              setOrderFailedMessage(params.message);
              setOrderFailed(true);
            },
          });
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10rem;
`;

const DivContent = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoint}) {
    flex-direction: row-reverse;
    > * {
      width: 50%;
    }
  }
`;

const DivItems = styled.div`
  > :first-child {
    margin-bottom: 1.25rem;
  }
  margin-bottom: 2.5rem;
`;

const DivAddress = styled.div`
  > div {
    > :first-child {
      margin-bottom: 1.25rem;
    }

    margin-bottom: 4.7rem;
  }
`;

const Ul = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const MarginBottom = styled.div`
  margin-bottom: 10px;
`;
